import i18n from "@/i18n";
import { AccountDetailData } from "../../data/accountDetailData";
import { ContractData } from "../../data/contractData";
import currencyFilter from "../../filters/currency.filter";
import sumFilter from "../../filters/sum.filter";
import dateFilter from "../../filters/date.filter";
import contractType from "../../filters/contractType.filter";
import { CardModel } from "./cardModel";
import { StatementModel } from "../statement/statementModel";
import { DepositForecastItemModel } from "../forecast/depositForecastItemModel";
import { dateFormat } from "../../utilities/parseHelper";
import { CreditScheduleModel } from "../schedule/creditScheduleModel";
import { replaceByTemplate } from "../../utilities/index";
import { appConfig } from "../../config/index";

/**  Модель договора-счета. */
export class ContractModel {
  public Data: ContractData;

  public StatementModel: StatementModel;

  public DepositForeCast: Array<DepositForecastItemModel> = [];

  public CreditSchedule: CreditScheduleModel = null;

  public Cards: CardModel[] = [];

  /** Символ валюты. */
  get Currency(): string {
    return currencyFilter(this.Data.CurrencyCharCode); //; FsHelper.currencySymb()
  }

  /**Реквизиты счета */
  public BankDetails: AccountDetailData | null = null;

  get ContractType(): string {
    return contractType(this.Data.ContractType);
  }

  /** Доступный остаток. */
  get DisposableLimit(): string {
    return sumFilter(this.Data.DisposableLimit);
  }

  get contractsControllerLabel(): string {
    const valTemplate: string = appConfig.contractsControllerLabelTemplate ?? "";
    const limit: string = `${this.DisposableLimit} ${this.Currency}`;
    const alias = this.Data.Alias;
    return valTemplate.length ? replaceByTemplate({Alias: alias, Limit: limit}, valTemplate) : `${alias} - ${limit}`;
  }

  /** Заблокированно на счёте. */
  get CalcLocked(): string {
    return sumFilter(this.Data.DebitHold);
  }

  /** Заблокированно на счёте. */
  get CalcLockedRetail(): string {
    return sumFilter(this.Data.CalcLocked);
  }

  /** Арестованная сумма. */
  get ArrestSum(): string {
    return sumFilter(this.Data.ArrestSum);
  }

  /** Остаток. */
  get Rest(): string {
    return sumFilter(this.Data.Rest);
  }

  /** Досрочное погашение. */
  get CanEarlyRepay(): string {
    return sumFilter(this.Data.CanEarlyRepay);
  }

  /** Сумма депозита. */
  get DepositSum(): string {
    return sumFilter(this.Data.DepositSum);
  }

  /** Процентная ставка. */
  get PercentRate(): string {
    return sumFilter(this.Data.PercentRate).toString() + " %";
  }

  /** Ставка при досрочном изъятии. */
  get EarlyPercent(): string {
    return sumFilter(this.Data.EarlyPercent).toString() + " %";
  }

  /** Дата открытия счета. */
  get DateOpen(): string {
    if (this.Data.DateOpen == null) return "";

    return dateFilter(this.Data.DateOpen, "date");
  }

  /** Дата закрытия счета. */
  get DateClose(): string {
    if (this.Data.DateClose == null || dateFilter(this.Data.DateClose, "date") == "01.01.1900") return "";

    return dateFilter(this.Data.DateClose, "date");
  }

  /** Срок закрытия счета. */
  get DateEnd(): string {
    if (this.Data.DateEnd == null || this.Data.DateClose != null) return "";

    return dateFilter(this.Data.DateEnd, "date");
    //return new Intl.DateTimeFormat("ru-RU", { day: "", month: "", year: ""}).format(new Date(this.Data.DateEnd));
  }

  /** Окончание срока вклада */
  get RangeTo(): string {
    if (this.Data.RangeTo == null) return "";

    return dateFilter(this.Data.RangeTo, "date");
  }

  /** Признак действующего счёта. */
  get IsOpen(): boolean {
    return this.Data.DateClose == null;
  }

  /** Имеется кредитный лимит. (Только для карточных счетов) */
  get IsUseCreditLimit(): boolean {
    return this.Data.FullLimit > 0 && this.IsOpen;
  }

  /**  Кредитный лимит не имеется. */
  get IsNotUseCreditLimit(): boolean {
    return (this.Data.FullLimit === null || this.Data.FullLimit <= 0) && this.IsOpen;
  }

  /** Кредитный лимит. (Только для карточных счетов) */
  get FullLimit(): string {
    return sumFilter(this.Data.FullLimit);
  }

  /** Использованные кредитные средства. (Только для карточных счетов) */
  get UsedLimit(): string {
    return sumFilter(this.Data.CalcUsedLimit);
  }

  /** Собственные средства. (Только для карточных счетов) */
  get PersonalFunds(): string {
    return sumFilter(this.Data.CalcEquity);
  }

  /** Кредитные средства. (Только для карточных счетов) */
  get CreditFunds(): string {
    return sumFilter(this.Data.CalcCredit);
  }

  /** Признак наличия сообственных средств */
  get IsHavePersonalFunds(): boolean {
    return (this.Data.DisposableLimit - this.Data.FullLimit) >=0;
  }

  /** Признак видимости формы задолжности */
  get IsViDebtForm(): boolean {
    return this.Data.OutstandingSum > 0 && !this.Data.DateClose;
  }

  /**
   * Признак отображения процентов.
   * При наличии кредитного лимита и даты последнего начисления.
   *  (Только для карточных счетов) */
  get IsViewPercents(): boolean {
    return (
      this.IsUseCreditLimit && this.Data.PercChargeDate != null && this.IsOpen
    );
  }

  /** Дата последнего расчета процентов. (Только для карточных счетов) */
  get PercChargeDate(): boolean {
    return dateFilter(this.Data.PercChargeDate, "date");
  }

  /** Не просроченные проценты по непросроченной ссуде. (Только для карточных счетов) */
  get PercOnMainSum(): string {
    return sumFilter(this.Data.PercOnMainSum);
  }

  /** Просроченные проценты по непросроченной ссуде. (Только для карточных счетов) */
  get OutPercOnMainSum(): string {
    return sumFilter(this.Data.OutPercOnMainSum);
  }

  /** Минимальный остаток по счету. */
  get MinDepositRest(): string {
    return sumFilter(this.Data.MinDepositRest);
  }

  /** Cумма очередного платежа по основному долгу. */
  get MinPaySumMain(): string {
    return sumFilter(this.Data.MinPaySumMain);
  }

  get MinPaySum(): string {
    return sumFilter(this.Data.MinPaySum);
  }

  /** Cумма очередного платежа по процентам. */
  get MinPaySumPercents(): string {
    return sumFilter(this.Data.MinPaySumPercents);
  }

  /** Сумма страхового платежа. */
  get AmountInsurance(): string {
    return sumFilter(this.Data.AmountInsurance);
  }

  /** Остаток задолженности на текущую дату по процентам. */
  get RestAcntPercents(): string {
    return sumFilter(this.Data.RestAcntPercents);
  }
  /** Полученный доход. */
  get IncomeSum(): string {
    return sumFilter(this.Data.IncomeSum);
  }
  /** Планируемый доход. */
  get FutureIncomeSum(): string {
    return sumFilter(this.Data.FutureIncomeSum);
  }

  /** (Только для карточных счетов) */
  get GraceOutstandingDate(): boolean {
    return dateFilter(this.Data.GraceOutstandingDate, "date");
  }

  /** Просрочка кредита */
  get OutstandingDate(): boolean {
    return dateFilter(this.Data.OutstandingDate, "date");
  }

  /** Расчёт ежемесячного платежа, чтобы не платить проценты. */
  get MonthGracePay(): number | null {
    if (this.Data.GraceSum && this.Data.GraceSum > 0)
      return this.Data.GraceSum + this.Data.GraceRepaidSum;
    return 0;
  }

  /** Просрочка кредита (дни) */
  get OutstandingDelayDate(): string {
    if (!this.Data.ActualDate || !this.Data.OutstandingDate)
      return "";
    let dtPer = this.Data.ActualDate?.valueOf() - this.Data.OutstandingDate?.valueOf();
    dtPer = parseInt((dtPer / 1000 / 60 / 60 / 24).toString());
    if (dtPer > 0) {
      return dtPer.toString();
    }
    return "";
  }

  /** (Только для карточных счетов) */
  get GraceEndDate(): boolean {
    return dateFilter(this.Data.GraceEndDate, "date");
  }

  
  /** (Только для карточных счетов) */
  get MinPayDate(): boolean {
    return dateFilter(this.Data.MinPayDate, "date");
  }

  /**Срок вклада (дней). */
  get DepositPeriod(): string {
    if (!this.Data.DateOpen || !this.Data.DateEnd || !this.Data.RangeFrom)
      return "";

    let dtPer = this.Data.DateEnd.valueOf() - this.Data.DateOpen.valueOf();
    dtPer = parseInt((dtPer / 1000 / 60 / 60 / 24).toString());
    //if (this.Data.DateOpen != this.Data.RangeFrom) dtPer += 1;

    return dtPer.toString();
  }

  /**Срок вклада (месяцев). */
  get DepositPeriodMonth(): string {
    if (!this.Data.DateOpen || !this.Data.DateEnd || !this.Data.RangeFrom)
      return "";

    let dtPer = this.Data.DateEnd.valueOf() - this.Data.RangeFrom.valueOf();
    dtPer = parseInt((dtPer / 1000 / 60 / 60 / 24 / 30).toString());

    if (this.Data.DateOpen != this.Data.RangeFrom) dtPer += 1;

    return dtPer.toString();
  }

  /**Выплата процентов */
  get PayPercentsPeriod(): string {
    return this.Data.PayPercentsPeriod;
  }

  /**Залог */
  get Pledge(): string {
    return this.Data.Pledge
  }

  /**Поручитель по кредиту */
  get Guarantor(): string {
    return this.Data.Guarantor
  }

  /** Признак наличия минимального платежа. */
  get isMinPaySum(): boolean {
    return this.IsOpen && this.Data.MinPayDate !== null && this.Data.MinPaySum !== null && this.Data.MinPaySum > 0;
  }

  /** Метод подсчитывания задолженности */
  public CalcSumDebt(): number | null { 
    if (this.Data.FullLimit <= 0) {
      return 0;
    }
  
    let sum = 0;
  
    if (this.Data.MinPaySum != null && this.Data.MinPaySum > 0 || (this.Data.OutstandingSum != null && this.Data.OutstandingSum > 0) || (this.Data.PenaltySum != null && this.Data.PenaltySum > 0))
    {
      if (this.Data.MinPaySum > 0)
        sum += this.Data.MinPaySum;

      if (this.Data.OutstandingSum > 0 || this.Data.PenaltySum > 0)
      {
        if (this.Data.OutstandingSum > 0)
          sum += this.Data.OutstandingSum;

        if (this.Data.PenaltySum > 0)
          sum += this.Data.PenaltySum;
      }
    }
  
    return sum;
  }

  /** Рассчёт минимального платежа. (Только для карточных счетов) */
  public GetMinPayInfo(isCustomDisplay: boolean = false): string {
    let text = "";

    if (this.Data.FullLimit <= 0) return text;

    let sum = 0;

    if (
      this.Data.MinPaySum > 0 ||
      this.Data.OutstandingSum > 0 ||
      this.Data.PenaltySum > 0
    ) {
      if (this.Data.MinPaySum > 0 && !isCustomDisplay) {

        text += i18n.t("main.contracts.details.minPaySumText", {
          minPaySum: `${(this.Data.MinPaySum + this.Data.MinPayRepaidSum).toFixed(2)} ${
            this.Currency
          }\n`
        });
        text += i18n.t("main.contracts.details.necessuaryPayText", {
          necessuaryPaySum: `${Math.max(0, this.Data.MinPaySum - (this.Data.Rest - this.Data.NonExecDraftSum)).toFixed(2)} ${
            this.Currency
          }`
        });

        if (this.Data.MinPayDate != null) {
          text += i18n.t("main.contracts.details.minPayDateText", {
            minPayDate: dateFilter(this.Data.MinPayDate, 'date')
          });
          const days =
            (this.Data.MinPayDate.getMilliseconds() - Date.now()) *
            1000 *
            60 *
            60 *
            24;
          if (days > 0)
            text += i18n.t("main.contracts.details.minPayDateDaysText", {
              minPayDateDays: days
            });
        }
        // text += ".";

        sum += this.Data.MinPaySum;
      }

      if (this.Data.MinPaySum > 0 && isCustomDisplay) {

        text += i18n.t("main.contracts.details.minPaySumText", {
          mainAccount: `${ this.Data.MainAccount }`
        });
        sum += this.Data.MinPaySum;

        if (this.Data.MinPayDate != null) {
          text += i18n.t("main.contracts.details.minPayDateText", {
            minPayDate: dateFilter(this.Data.MinPayDate, 'date')
          });
        }
      }

      if (this.Data.OutstandingSum > 0 || this.Data.PenaltySum > 0) {
        if (this.Data.OutstandingSum > 0) {
          text += i18n.t("main.contracts.details.outstandingSumText", {
            outstandingSum: `${this.Data.OutstandingSum} ${this.Currency}\n`
          });
          sum += this.Data.OutstandingSum;
        }

        if (this.Data.PenaltySum > 0) {
          text += i18n.t("main.contracts.details.penaltySumText", {
            penaltySum: `${this.Data.PenaltySum} ${this.Currency}\n`
          });
          sum += this.Data.PenaltySum;
        }
        text += i18n.t("main.contracts.details.itogoMinPayText", {
          itogoMinPay: `${sum} ${this.Currency}\n`
        });
      }
    } else text += i18n.t("main.contracts.details.noMinPayText");

    return text;
  }

  constructor(contractData: ContractData | null = null) {
    if (!contractData) {
      this.Data = new ContractData();
    } else {
      this.Data = contractData;
      this.StatementModel = new StatementModel({
        minDate: this.Data.DateOpen,
        maxDate: this.Data.ContractType === 5 ? this.Data.DateEnd : this.Data.DateClose,
        token: "ContractStatementService",
        title: i18n.t("main.operations.contractStatement", {
          name: this.Data.Alias
        }),
        isChart:  this.Data.ContractType !== 5,
        isPeriod: this.Data.ContractType === 5,
        statusBar: [1],
        contractType: this.Data.ContractType
      });

      for (const i in contractData.Cards) {
        this.Cards.push(new CardModel(contractData.Cards[i]));
        this.Cards[this.Cards.length - 1].StatementModel = new StatementModel({
          minDate: this.Data.DateOpen,
          maxDate: this.getMaxDateForCard(this.Cards[this.Cards.length - 1]),
          token: "CardStatementService",
          title: i18n.t("main.operations.cardStatement", {
            name: this.Cards[this.Cards.length - 1].Data.Alias
          })
        });
      }
    }
  }

  /**Обновить счет */
  public update(contractData: ContractData): void {
    this.Data.update(contractData);
  }

  /** Преобразуем дату закрытия или открытия карты(счета) в подхродящуюю для пикера */
  private getDate(dateBase: string | Date) {
    let date = "";
    if (!dateBase) return "-";
    else date = dateBase.toString();
    let result = null;
    /** Если формат /(Date)/ */
    if (
      date &&
      new Date(date).toString() === "Invalid Date" &&
      (date as string).length !== 5
    ) {
      result = dateFormat(date, "daterev");
      /** Если формат 02/20 */
    } else if (date) {
      const month = (date as string).substring(0, 2);
      const year =
        new Date()
          .getFullYear()
          .toString()
          .substring(0, 2) + (date as string).substring(3);
      const resDate =
        new Date(+year, +month, 0).getDate() < 10
          ? `0${new Date(+year, +month, 0).getDate()}`
          : new Date(+year, +month, 0).getDate();
      result = `${year}-${month}-${resDate}`;
    }
    if (new Date(result).getFullYear() !== 1970) return result;
    else return "-";
  }

  private getMaxDateForCard(card: CardModel): string | Date {
    let res = false;
    if (!this.Data.DateClose || this.Data.DateClose) return this.getDate(card.Data.ExpDate);
    if (
      new Date(dateFormat(this.Data.DateClose, "daterev")) <
      new Date(this.getDate(card.Data.ExpDate))
    ) {
      res = true;
    }
    return res ? this.Data.DateClose : this.getDate(card.Data.ExpDate);
  }
}
